<template>
  <div class="v-select-list__wrapper" :class="{ [extraClass]: !!extraClass }">
    <VInput
      placeholder="Filter..."
      :value="inputValue"
      :classType="classType"
      :name="name"
      :label="label"
      :errors="errorsAppear"
      :required="isRequired"
      icon="filter"
      @input="onInput"
      @blur="onBlur"
      @keyup.enter.native="onEnter"
      @focus="onFocus"
    >
    </VInput>

    <WithLoader :isLoading="isLoading">
      <div class="select-ul__outer">
        <div
          class="select-ul__wrapper"
          :class="{
            ['add-new-tag']: _createTagState,
            ['no-results']: !filteredItems.length
          }"
        >
          <ul
            :class="{
              [classType]: true,
              ['extended']: filteredItems.length > 10
            }"
          >
            <li
              class="empty-tag"
              v-if="!filteredItems.length && inputValue === ''"
            >
              Start typing in the input and create tag!
            </li>

            <li v-if="_createTagState" class="add">
              Press enter to add
              <span class="add-value"> "{{ this.inputValue }}" </span> to the
              tag list
            </li>

            <li
              v-if="filteredItems.length"
              v-for="item in filteredItems"
              :key="item.key"
              :class="getItemClass(item.key)"
            >
              <span @click="onSelect(item.key, item[valueField])">{{
                item.label
              }}</span>
              <i
                v-show="withDelete"
                class="fal fa-times"
                @click="e => removeElement(e, item)"
              ></i>
            </li>
          </ul>
        </div>
      </div>
    </WithLoader>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import VInput from "@/components/VInput";
import WithLoader from "@/components/WithLoader";

import { maxLength } from "@/utils/validations";

import omit from "lodash/omit";
import mapKeys from "lodash/mapKeys";
import pick from "lodash/pick";

import { debounce } from "lodash";

//mixins
import withToasted from "@/mixins/withToasted";

export default Vue.component("VSelectList", {
  props: {
    items: VueTypes.arrayOf(VueTypes.object).isRequired,
    valueField: VueTypes.string.def("value"),
    keyField: VueTypes.string.def("key"),
    labelField: VueTypes.string.def("label"),
    label: VueTypes.string,
    multiple: VueTypes.bool.def(false),
    name: VueTypes.string,
    onAddHandler: VueTypes.func,
    onRemoveHandler: VueTypes.func,
    withDelete: VueTypes.bool.def(false),
    classType: VueTypes.oneOf(["primary", "secondary", "tertiary", "dark"]).def(
      "primary"
    ),
    selectedItems: VueTypes.arrayOf(VueTypes.string).def([]),
    errors: VueTypes.arrayOf(VueTypes.string).def([]),
    extraClass: VueTypes.string.def(""),
    isRequired: VueTypes.bool,
    maxInputLength: VueTypes.number,
    onSearchAsyncHandler: VueTypes.any,
    isLoading: VueTypes.bool.def(false)
  },

  components: {
    VInput,
    WithLoader
  },

  mixins: [withToasted],

  data() {
    return {
      inputValue: "",
      localSelected: this.selectedItems,
      prevSelected: [],
      filteredItems: [],
      inputErrors: [],
      isInputActive: false,
      fetchedTagsBySearch: []
    };
  },

  methods: {
    _filterItems() {
      // current selected items
      const localSelectedIds = Array.from(this.prevSelected);

      // copy of the initial items object
      const copyItems = Array.from(this.items);

      // copy with reorganized format
      // [{ key: 'someId', ...anotherProps }] -> { 'someId': { key: 'someId', ...anotherProps }}
      const mapKeyedCopy = mapKeys(copyItems, "key");

      const itemsWithoutSelected = omit(mapKeyedCopy, localSelectedIds);
      const selectedObjects = pick(mapKeyedCopy, localSelectedIds);

      const data = [
        ...Object.values(selectedObjects),
        ...Object.values(itemsWithoutSelected)
      ];

      return data.filter(
        item =>
          item.label.toLowerCase().indexOf(this.inputValue.toLowerCase()) !== -1
      );
    },

    _select(key) {
      if (!this.multiple) {
        this.localSelected = [key];
      } else {
        const itemIdx = this.localSelected.indexOf(key);
        if (itemIdx !== -1) {
          this.localSelected.splice(itemIdx, 1);
        } else {
          this.localSelected.push(key);
        }
      }
    },

    onEnter(e) {
      e.stopPropagation();

      if (this._createTagState) {
        this.addItem();
      }
    },

    onSelect(key) {
      this._select(key);

      if (this.inputValue) {
        this.inputValue = "";
      }

      const localSelected = Array.from(this.localSelected);
      this.prevSelected = Array.from(
        new Set([...this.prevSelected, ...localSelected])
      );

      this.$emit("select", localSelected);
    },

    onInput(val) {
      if (this.onSearchAsyncHandler) {
        this.debounceSearchTags(val);
      }

      this.inputValue = val;
    },

    debounceSearchTags: debounce(function(val) {
      this.onSearchAsyncHandler(val).then(resp => {
        this.filteredItems = resp.map(tag => {
          return { key: tag.id, value: tag.id, label: tag.name };
        });
      });
    }, 300),

    getItemClass(key) {
      return this.localSelected.indexOf(key) !== -1 ? "active" : "";
    },

    addItem() {
      const newTag = {
        name: this.inputValue,
        value: this.inputValue,
        key: `new-${this.inputValue.trim().toLowerCase()}`
      };

      // reset input
      // this._select(newTag.key);
      // this.inputValue = "";

      this.onAddHandler ? this.onAddHandler(newTag, this.inputValue) : null;
      this.$emit("add", this.inputValue);
    },

    removeElement(e, item) {
      /*wait for response then delete tag locally*/
      if (this.onRemoveHandler) {
        this.onRemoveHandler(item)
          .then(() => {
            e.stopPropagation();
            let i = this.filteredItems.map(item => item.key).indexOf(itemKey);
            this.filteredItems.splice(i, 1);
          })
          .catch(() => this.toast("Something went wrong!", "error"));

        /*just delete tag locally*/
      } else {
        e.stopPropagation();
        let i = this.filteredItems.map(item => item.key).indexOf(item.key);
        this.filteredItems.splice(i, 1);
      }
    },

    onBlur() {
      this.isInputActive = false;
      this.$emit("blur");
      if (this.maxInputLength) {
        let validationMessage = maxLength(this.maxInputLength)(this.inputValue);
        if (
          validationMessage &&
          !this.inputErrors.includes(validationMessage)
        ) {
          this.inputErrors.push(validationMessage);
        }
      }

      return this.$emit("error", this.inputErrors);
    },

    onFocus() {
      this.$emit("focus");
      this.isInputActive = true;
    }
  },

  computed: {
    _createTagState() {
      return this.inputValue !== "" && !this.tagExists;
    },

    tagExists() {
      return (
        this.filteredItems.length &&
        this.filteredItems.filter(item => item.label === this.inputValue)[0]
      );
    },

    errorsAppear() {
      return !this.isInputActive ? this.errors : [];
    }
  },

  created() {
    this.filteredItems = this._filterItems();
  },

  updated() {
    this.localSelected = this.selectedItems;
  },

  watch: {
    // inputValue: function() {
    //   this.filteredItems = this._filterItems();
    // },

    items: function() {
      this.filteredItems = this._filterItems();
    }
  }
});
</script>

<style lang="scss" scoped>
.v-select-list__wrapper {
  width: 100%;

  /deep/ .input-container {
    margin-bottom: 15px;
  }
}

.select-ul__wrapper {
  /* margin-top: 10px; */
  max-height: 200px;
  overflow: auto;

  // Shadows at top & bottom while scrolling if there any content
  background: linear-gradient(white 30%, rgba(255, 255, 255, 0)),
    linear-gradient(rgba(255, 255, 255, 0), white 70%) 0 100%,
    /* Shadows */
      radial-gradient(
        farthest-side at 50% 0,
        rgba(87, 87, 245, 0.5),
        rgba(87, 87, 245, 0)
      ),
    radial-gradient(
        farthest-side at 50% 100%,
        rgba(87, 87, 245, 0.5),
        rgba(87, 87, 245, 0)
      )
      0 100%;
  background-repeat: no-repeat;
  background-size: 100% 40px, 100% 40px, 100% 14px, 100% 14px;

  /* Opera doesn't support this in the shorthand */
  background-attachment: local, local, scroll, scroll;

  /* width */
  &::-webkit-scrollbar {
    display: block;
    width: 7px;
    height: 8px;
  }

  /* Track */
  &::-webkit-scrollbar-track {
    background: $primary-grey;
    border: 3px solid transparent;
    background-clip: content-box;
  }

  /* Handle */
  &::-webkit-scrollbar-thumb {
    background: $primary-color;
    border-radius: 3px;
  }

  /* Handle on hover */
  &::-webkit-scrollbar-thumb:hover {
    background: $primary-color;
  }

  &.add-new-tag {
    background: none;

    &.no-results .add {
      align-items: center;
      margin-bottom: 0;
    }
  }
}

ul {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  color: $button-dark-color;

  &.extended {
    justify-content: flex-start;
  }

  li {
    position: relative;
    cursor: pointer;
    border-radius: 4px;
    display: inline-block;
    margin-right: 10px;
    margin-bottom: 10px;
    transition: background-color 0.15s linear, color 0.15s linear;
    border: 1px solid #c5c5c5;
    color: #4e4e4e;
    font-family: "Lato-Light", sans-serif;

    font-size: 15px;
    line-height: 18px;
    padding: 7px 20px;

    i {
      position: absolute;
      top: 5px;
      right: 5px;
      display: none;
    }

    &:hover {
      background-color: transparent;
      color: $secondary-text-color;
      /*padding-right: 0;*/

      i {
        display: inline-block;
        font-size: 10px;
        color: $primary-color;
        vertical-align: top;
      }
    }

    &.add {
      display: flex;
      /* align-items: center; */
      border: none;
      cursor: default;
      padding: 0;
      height: 30px;
      margin-bottom: 10px;
      margin-right: 0;
      width: 100%;

      i {
        margin-right: 5px;
      }

      .add-value {
        margin: 0 5px;
        font-weight: 700;
        font-family: Lato-Regular, sans-serif;
      }
    }
  }

  &.primary {
    li.active {
      background-color: $primary-color;
      border-color: $primary-color;
      color: $white;

      i {
        color: $white;
      }
    }

    li:hover {
      border-color: $primary-color;
    }
  }

  & li.empty-tag {
    &,
    &:hover {
      border: none;
      background-color: transparent;
      color: $secondary-text-color;
      padding: 0 5px;
      cursor: default;
    }
  }
}
</style>
